<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcResourceStatusCard',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    resource: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.resource,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('resource');
    }
  );

  const act_3343864469950044_emit_byll7kgcb_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_3343864469950044_request_2n79uazwe_getActionArgs = async (
    $event
  ) => {
    return {
      url: props.resource.links.self,
      method: 'patch',
      data: {
        etag: props.resource.etag,
        inactive: !props.resource.inactive,
      },
    };
  };
  const act_1889980785424776_emit_hk5dnxrta_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_1889980785424776_request_xhow5jplc_getActionArgs = async (
    $event
  ) => {
    return {
      url: props.resource.links.self,
      method: 'patch',
      data: {
        etag: props.resource.etag,
        inactive: !props.resource.inactive,
      },
    };
  };

  const act_7315092382398562_reloadComponentData_r9s773sdr =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_7571bu8rb =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_r9s773sdr,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_7571bu8rb],
    event: 'reload',
  });
  const act_3343864469950044_emit_byll7kgcb = new actionTypes.EmitAction({
    actionArgs: act_3343864469950044_emit_byll7kgcb_getActionArgs,
  });
  const inst_evh_3343864469950044_success_pdpim73du =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_emit_byll7kgcb,
      event: 'success',
      displayName: 'emit',
    });
  const act_3343864469950044_request_2n79uazwe = new actionTypes.RequestAction({
    actionArgs: act_3343864469950044_request_2n79uazwe_getActionArgs,
    displayName: 'toggleState',
    events: [inst_evh_3343864469950044_success_pdpim73du],
  });
  const inst_evh_3343864469950044_clickStopPrevent_q7yjvbnd3 =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_request_2n79uazwe,
      event: 'click.stop.prevent',
      displayName: 'toggleState',
    });
  const inst_evh_3343864469950044_clickStopPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3343864469950044_clickStopPrevent_q7yjvbnd3],
      event: 'click.stop.prevent',
    });
  const act_1889980785424776_emit_hk5dnxrta = new actionTypes.EmitAction({
    actionArgs: act_1889980785424776_emit_hk5dnxrta_getActionArgs,
  });
  const inst_evh_1889980785424776_success_q21ddsvoq =
    new actionTypes.EventHandlerImpl({
      action: act_1889980785424776_emit_hk5dnxrta,
      event: 'success',
      displayName: 'emit',
    });
  const act_1889980785424776_request_xhow5jplc = new actionTypes.RequestAction({
    actionArgs: act_1889980785424776_request_xhow5jplc_getActionArgs,
    displayName: 'toggleState',
    events: [inst_evh_1889980785424776_success_q21ddsvoq],
  });
  const inst_evh_1889980785424776_clickStopPrevent_gp3v69xrd =
    new actionTypes.EventHandlerImpl({
      action: act_1889980785424776_request_xhow5jplc,
      event: 'click.stop.prevent',
      displayName: 'toggleState',
    });
  const inst_evh_1889980785424776_clickStopPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1889980785424776_clickStopPrevent_gp3v69xrd],
      event: 'click.stop.prevent',
    });

  const actions = {
    inst_evh_7315092382398562_reload_7571bu8rb,
    inst_evh_7315092382398562_reload,
    inst_evh_3343864469950044_success_pdpim73du,
    inst_evh_3343864469950044_clickStopPrevent_q7yjvbnd3,
    toggleState: inst_evh_3343864469950044_clickStopPrevent_q7yjvbnd3,
    inst_evh_3343864469950044_clickStopPrevent,
    inst_evh_1889980785424776_success_q21ddsvoq,
    inst_evh_1889980785424776_clickStopPrevent_gp3v69xrd,
    inst_evh_1889980785424776_clickStopPrevent,
  };

  const evh_7315092382398562_reload_7571bu8rb = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_7571bu8rb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_success_pdpim73du = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_success_pdpim73du.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_clickStopPrevent_q7yjvbnd3 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3343864469950044_clickStopPrevent_q7yjvbnd3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_clickStopPrevent = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_clickStopPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1889980785424776_success_q21ddsvoq = async (event, scope) => {
    return await actions.inst_evh_1889980785424776_success_q21ddsvoq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1889980785424776_clickStopPrevent_gp3v69xrd = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1889980785424776_clickStopPrevent_gp3v69xrd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1889980785424776_clickStopPrevent = async (event, scope) => {
    return await actions.inst_evh_1889980785424776_clickStopPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpBox spacing="pe-3 py-1" alignItems="center" v-if="loaded">
      <ItpBadge
        :text="resource.inactive ? gettext('Inactive') : gettext('Active')"
        :variant="resource.inactive ? 'light' : 'primary'"
        no-box-shadow
      >
      </ItpBadge>
      <ItpButton
        size="sm"
        variant="light"
        :text="pgettext('UcResourceStatusCard', 'Activate')"
        class="mx-3 border"
        v-if="!resource.locked && resource.inactive"
        @click.stop.prevent="evh_3343864469950044_clickStopPrevent($event, {})"
      >
      </ItpButton>
      <ItpButton
        size="sm"
        variant="light"
        :text="pgettext('UcResourceStatusCard', 'Inactivate')"
        class="mx-3 border"
        v-if="!resource.locked && !resource.inactive"
        @click.stop.prevent="evh_1889980785424776_clickStopPrevent($event, {})"
      >
      </ItpButton>
    </ItpBox>
  </ItpUsercontrol>
</template>
